import { inject, injectable } from "inversify";
import { NotifyData } from "../../data/notifyData";
import { PersonInfoModel } from "../../models/person/personInfoModel";
import { AuthorizationsHistoryData } from "../../data/authorizationsHistoryData";
import { ReferenceData } from "../../data/referenceData";
import IPersonService from "./IPersonService";
import { PersonApi } from "../api/PersonApi";
import i18n from "@/i18n";
import IAlertService from "../alert/IAlertService";
import { DiTokens } from "../diTokens";
import IErrorService from "../error/IErrorService";

@injectable()
/** Сервис данных клиента */
export default class PersonService implements IPersonService {
  constructor(
    @inject(DiTokens.AlertService) private alertService: IAlertService,
    @inject(DiTokens.ErrorService) private errorService: IErrorService
  ) {}

  /**
   * Получение данных Пользователя
   * @returns Объект PersonInfoModel
   */
  public async fetchPersonInfo(): Promise<PersonInfoModel> {
    let resp: PersonInfoModel = new PersonInfoModel();
    try {
      resp = await PersonApi.syncPersonInfo();
    } catch (e) {
      await this.errorService.errorHandler(e, true, true, "");
    }
    return resp;
  }

  public async fetchReferences(requestLink: string): Promise<Array<ReferenceData>> {
    let resp: Array<ReferenceData> = [];
    try {
      resp = await PersonApi.getReferences(requestLink);
    } catch(e: any) {
      this.alertService.error(e.message);
    }
    return resp;
  }

  /**
   * Получение уведомлений Пользователя
   * @returns Объект NotifyData
   */

  public async fetchNotificationsData(): Promise<Array<NotifyData>> {
    let resp: Array<NotifyData> = [];
    try {
      resp = await PersonApi.syncNotificationsData();
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return resp;
  }

  public async fetchNotifyDetails(notify: NotifyData): Promise<NotifyData> {
    try {
      const resNotify = await PersonApi.getNotifyDetail(notify.Id);
      if (notify.State != 2) await PersonApi.checkNotify(notify.Id);

      return resNotify;
    } catch (e) {
      await this.errorService.errorHandler(
        e,
        false,
        true,
        i18n
          .t("main.notifications.errors.loadError", { errorText: e.message })
          .toString()
      );
      throw e;
    }
  }

  public async deleteNotify(id: number): Promise<Array<NotifyData>> {
    try {
      await PersonApi.deleteNotify(id);
      this.alertService.success("Сообщение удалено");
      return this.fetchNotificationsData();
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
      throw e;
    }
  }

  public async setAlias(alias: string): Promise<any> {
    try {
      const res = await PersonApi.syncSetAlias(alias);
      if (res.result === "ok") {
        this.alertService.success(
          i18n.t("main.settings.messages.personAliasEdited").toString()
        );
        return alias;
      }
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
      return null;
    }
  }

  public async setAvatar(file: any): Promise<any> {
    try {
      if (file.size > 10 * 1024 * 1024) {
        this.alertService.error(
          i18n.t("main.profile.errors.tooBigImageError").toString()
        );
        return null;
      }

      const base64image = await PersonApi.syncSetAvatar(file);
      if (!base64image) {
        this.alertService.error(
          i18n.t("main.profile.errors.setAvatarErrorEmpty").toString()
        );
        return null;
      } else return base64image;
    } catch (e) {
      await this.errorService.errorHandler(
        e,
        false,
        true,
        i18n
          .t("main.profile.errors.setAvatarError", { errorText: e.message })
          .toString()
      );
      return null;
    }
  }

  public async getAuthorizationsHistory(
    take: number,
    skip: number,
    dateBegin: string,
    dateEnd: string
  ): Promise<Array<AuthorizationsHistoryData>> {
    try {
      const res = await PersonApi.authHistory(take, skip, dateBegin, dateEnd);
      return res;
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
      throw e;
    }
    return [];
  }

  public async removeAvatar(): Promise<boolean> {
    try {
      const res = await PersonApi.removeAvatar();
      return res;
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
      return false;
    }
  }
}
